import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'

const BOTTOM_THRESHOLD = 80
const TOP_THRESHOLD = 160

interface Options {
  /** Changes whenever the rendered entries change. */
  firstKey: string | undefined
  lastKey: string | undefined
  count: number
  /** Whether there is older history to load: without it, reaching the top has nothing to keep the reader's place for. */
  hasMore: boolean
  onReachTop: () => Promise<unknown> | void
}

/**
 * Stick-to-bottom behaviour, prepend anchoring for older history and "jump to latest".
 */
export function useChatScroll({ firstKey, lastKey, count, hasMore, onReachTop }: Options) {
  const containerRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const atBottomRef = useRef(true)
  const [atBottom, setAtBottom] = useState(true)
  const anchor = useRef<{ height: number; top: number; firstKey: string | undefined } | null>(null)
  const loadingTop = useRef(false)
  const initialised = useRef(false)
  const onReachTopRef = useRef(onReachTop)

  useEffect(() => {
    onReachTopRef.current = onReachTop
  }, [onReachTop])

  const scrollToBottom = useCallback(() => {
    const el = containerRef.current
    if (!el) return
    el.scrollTop = el.scrollHeight
    atBottomRef.current = true
    setAtBottom(true)
  }, [])

  const triggerTop = useCallback(() => {
    const el = containerRef.current
    // An anchor waits for older entries to arrive, and holding the bottom waits with it: with none to come it would wait for good.
    if (!el || loadingTop.current || !hasMore) return
    loadingTop.current = true
    anchor.current = { height: el.scrollHeight, top: el.scrollTop, firstKey }
    Promise.resolve(onReachTopRef.current()).finally(() => {
      loadingTop.current = false
    })
  }, [firstKey, hasMore])

  const onScroll = useCallback(() => {
    const el = containerRef.current
    if (!el) return
    const bottom = el.scrollHeight - el.scrollTop - el.clientHeight < BOTTOM_THRESHOLD
    if (bottom !== atBottomRef.current) {
      atBottomRef.current = bottom
      setAtBottom(bottom)
    }
    if (el.scrollTop < TOP_THRESHOLD) triggerTop()
  }, [triggerTop])

  // After entries change: keep the view anchored when history was prepended, else stick to bottom.
  useLayoutEffect(() => {
    const el = containerRef.current
    if (!el || count === 0) return
    if (!initialised.current) return
    const saved = anchor.current
    if (saved && saved.firstKey !== firstKey) {
      el.scrollTop = saved.top + (el.scrollHeight - saved.height)
      anchor.current = null
      return
    }
    if (atBottomRef.current) el.scrollTop = el.scrollHeight
  }, [firstKey, lastKey, count])

  // Images and late layout changes, and the list itself getting shorter as a keyboard comes up: stay pinned to the bottom
  // when the user was there.
  useEffect(() => {
    const content = contentRef.current
    const el = containerRef.current
    if (!content || !el || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(() => {
      if (atBottomRef.current && !anchor.current) el.scrollTop = el.scrollHeight
    })
    observer.observe(content)
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const markInitialised = useCallback(() => {
    initialised.current = true
  }, [])

  return { containerRef, contentRef, onScroll, atBottom, atBottomRef, scrollToBottom, markInitialised, initialised }
}
